import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useUser } from '../hooks/UserContext';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import { SyncClient, SyncStatus } from './client';
import { fineStore } from './fines';
import * as persist from './persist';
import { syncStore } from './store';
import { WriteFailure, writes } from './writes';

/**
 * Runs the sync client and the write queue for whoever is signed in
 * (back-end-rs/docs/SYNC_ENGINE.md §7). Mounted once, in App.
 *
 * Keyed on the user id: a different person signing in in the same tab gets
 * a fresh pool, and signing out empties the pool and deletes every copy in
 * IndexedDB — unsent writes included — before anything else can read it.
 */
const SyncStatusContext = createContext<SyncStatus>('idle');

export const useSyncStatus = (): SyncStatus => useContext(SyncStatusContext);

const SyncHoldingContext = createContext(false);

/**
 * True while the signed-in app waits for its first copy (see `holding`
 * below). Something that would fetch for itself during the hold — the
 * sidebar's company and associations — waits on this instead.
 */
export const useSyncHolding = (): boolean => useContext(SyncHoldingContext);

/**
 * Where the hold takes effect: while it lasts, `fallback` renders in place
 * of the children, so no view mounts and fetches what the bootstrap is
 * about to bring.
 *
 * A boundary of its own rather than part of `SyncProvider`, so that it can
 * sit inside the providers a fallback needs — `App` puts it inside the ⌘K
 * palette's, because the fallback is the signed-in shell (#667). It used
 * to be a bare spinner in place of the whole app, which blanked the shell
 * between signing in and the first page.
 */
export const SyncHold: React.FC<{
  fallback: React.ReactNode;
  children: React.ReactNode;
}> = ({ fallback, children }) => (
  <>{useSyncHolding() ? fallback : children}</>
);

/**
 * How long a page waits for a copy that is on its way before fetching for
 * itself. Without the wait, a first load fetched everything twice: every
 * view's REST fallback ran alongside the bootstrap it was about to be
 * given. Longer than a bootstrap takes; short enough that a broken engine
 * costs a moment, not a page.
 */
const FIRST_COPY_WAIT_MS = 4000;

const UNSENT_NOTICE_MS = 1500;

export const SyncProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userId } = useUser();
  const [status, setStatus] = useState<SyncStatus>('idle');
  const lastUser = useRef<string | null>(null);
  const ready = useSyncExternalStore(syncStore.subscribe, syncStore.isReady);
  const [waited, setWaited] = useState(false);

  useEffect(() => {
    setWaited(false);
    if (!userId) {
      writes.stop();
      syncStore.clear();
      fineStore.clear();
      setStatus('idle');
      persist.removeAll(undefined, lastUser.current ?? undefined);
      lastUser.current = null;
      return;
    }
    if (lastUser.current && lastUser.current !== userId) {
      syncStore.clear();
      fineStore.clear();
    }
    lastUser.current = userId;
    // Anyone else's copy on this browser goes, whoever it belonged to.
    persist.removeAll(userId);

    const client = new SyncClient(syncStore, userId);
    const unsubscribe = client.onStatus(setStatus);
    client.start();
    writes.start(userId);
    const timer = setTimeout(() => setWaited(true), FIRST_COPY_WAIT_MS);
    return () => {
      clearTimeout(timer);
      unsubscribe();
      client.stop();
      writes.stop();
    };
  }, [userId]);

  // Signed in, no copy yet, and one still coming: hold the page briefly
  // rather than let every view fetch what the bootstrap is about to bring.
  // `error` means it is not coming; views fetch at once. `SyncHold` is
  // where the hold is applied.
  const holding =
    !!userId && !ready && !waited && (status === 'idle' || status === 'bootstrapping');

  return (
    <SyncStatusContext.Provider value={status}>
      <SyncHoldingContext.Provider value={holding}>
        {children}
        <WriteStatus />
      </SyncHoldingContext.Provider>
    </SyncStatusContext.Provider>
  );
};

/**
 * Changes that are not yet saved, and changes the server refused that no
 * page was there to report — a write restored after a reload. A refusal
 * seen by the page that made it is shown by that page.
 */
const WriteStatus: React.FC = () => {
  const unsent = useSyncExternalStore(writes.subscribePending, writes.unsentCount);
  const [failures, setFailures] = useState<WriteFailure[]>([]);
  useEffect(() => writes.onFailure((f) => setFailures((prev) => [...prev, f])), []);

  // Every save is unsent for the length of its request; only one that is
  // still waiting after that is worth a word.
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (unsent === 0) {
      setSlow(false);
      return;
    }
    const timer = setTimeout(() => setSlow(true), UNSENT_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [unsent]);

  if ((unsent === 0 || !slow) && failures.length === 0) return null;
  return (
    <div className="fixed bottom-4 right-4 z-50 flex w-80 flex-col gap-2">
      {failures.map((f, i) => (
        <ErrorAlert
          key={i}
          className="bg-raised shadow-lg"
          title="A change was not saved"
          onDismiss={() => setFailures((prev) => prev.filter((x) => x !== f))}
        >
          {f.message}
        </ErrorAlert>
      ))}
      {unsent > 0 && slow && (
        <p
          role="status"
          className="rounded-md border border-line bg-raised px-3 py-2 text-[12px] text-fg-muted shadow-lg"
        >
          {unsent === 1 ? '1 change' : `${unsent} changes`} waiting to be saved
        </p>
      )}
    </div>
  );
};
